import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DynamicSearchBar from "../Users/CreateNewUser/components/DynamicSearchbar";
import TeamDetails from "./TeamDetails";
import ManagementPage from "../shared/ManagementPage";
import {
  getAllClientActivate,
  getProjectByClientId,
} from "../../core/services/client.service";
import { getAllTeams, updateTeam } from "../../core/services/team.service";
import { getUsersByClient } from "../../core/services/keycloakApi";
import { getProfileByUserId } from "../../core/services/profile.service";

export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#f5f5f5",
    fontSize: 14,
    height: 46,
    borderRadius: 2,
    background: "rgba(255, 255, 255, 0.04)",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(245, 245, 245, 0.8)",
      borderWidth: "1.5px",
    },
    "&.Mui-focused": {
      background: "rgba(255, 255, 255, 0.08)",
    },
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.35)",
      background: "rgba(255, 255, 255, 0.035)",
    },
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "46px !important",
    boxSizing: "border-box",
    py: "0 !important",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 13,
    "&.Mui-focused": {
      color: "#f5f5f5",
    },
  },
  "& .MuiSvgIcon-root": {
    color: "rgba(255, 255, 255, 0.6)",
  },
};

export const selectMenuProps = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
  PaperProps: {
    sx: {
      mt: 1,
      maxHeight: 260,
      background:
        "linear-gradient(135deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.08) 100%)",
      backdropFilter: "blur(15px)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      borderRadius: 2,
      color: "#f5f5f5",
      boxShadow: "0 12px 30px rgba(0, 0, 0, 0.3)",
      "& .MuiList-root": {
        py: 0.75,
      },
      "& .MuiMenuItem-root": {
        fontSize: 14,
        minHeight: 40,
        py: 1.2,
        "&:hover": {
          background: "rgba(255, 255, 255, 0.12)",
        },
        "&.Mui-selected": {
          background: "rgba(250, 204, 21, 0.18)",
          fontWeight: 600,
          "&:hover": {
            background: "rgba(250, 204, 21, 0.24)",
          },
        },
      },
    },
  },
};

const actionButtonSx = {
  color: "white",
  borderColor: "rgba(255,255,255,0.3)",
  fontSize: 11,
  px: 1.2,
};

const getTeamKey = (team, index) =>
  team.teamId ?? (team.teamName || `team-${index}`);

const getProjectClientId = (project) =>
  project?.clientId ?? project?.ClientId ?? project?.client_id ?? null;

const loadProfileImageUrl = async (userId) => {
  if (!userId) return "";

  try {
    const response = await getProfileByUserId(userId);
    const blob = response?.data;

    if (!(blob instanceof Blob) || blob.size === 0) {
      return "";
    }

    return URL.createObjectURL(blob);
  } catch {
    return "";
  }
};

const getUserLabel = (user) => {
  if (!user) return "";

  return user.name || user.uname || user.email || "User";
};

const normalizeApiUser = (user) => {
  if (!user) return null;

  const uid = user.id ?? user.uid ?? user.userId ?? user.sub ?? "";
  if (!uid) return null;

  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  const name =
    user.name || `${firstName} ${lastName}`.trim() || user.username || "";

  return {
    uid,
    id: uid,
    uname: user.username || user.uname || user.preferred_username || "",
    name,
    firstName,
    lastName,
    email: user.email || "",
    enabled: user.enabled,
    raw: user,
  };
};

const TEAM_TABS = [
  { value: 1, icon: <GroupsOutlinedIcon />, label: "View Team" },
  { value: 0, icon: <GroupAddOutlinedIcon />, label: "Create Team" },
];

export default function TeamPage() {
  const profileImageUrlsRef = useRef([]);
  const [tabIndex, setTabIndex] = useState(1);
  const [clients, setClients] = useState([]);
  const [projectCatalog, setProjectCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [editForm, setEditForm] = useState({
    clientId: "",
    projectId: "",
    teamName: "",
  });
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingTeam, setUpdatingTeam] = useState(false);
  const [activeTeamAction, setActiveTeamAction] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const selectedClient = useMemo(
    () =>
      clients.find(
        (client) => String(client.clientId) === String(selectedClientId),
      ),
    [clients, selectedClientId],
  );

  const selectedUsers = useMemo(
    () =>
      users.filter((user) =>
        selectedUserIds.some((userId) => String(user.uid) === String(userId)),
      ),
    [selectedUserIds, users],
  );

  const revokeProfileImageUrls = useCallback(() => {
    profileImageUrlsRef.current.forEach((imageUrl) => {
      URL.revokeObjectURL(imageUrl);
    });
    profileImageUrlsRef.current = [];
  }, []);

  const selectedTeam = useMemo(() => {
    if (!activeTeamAction?.teamId) return null;

    return (
      teams.find(
        (team, index) =>
          String(getTeamKey(team, index)) === String(activeTeamAction.teamId),
      ) || null
    );
  }, [activeTeamAction, teams]);

  const selectedTeamProject = useMemo(() => {
    if (!selectedTeam?.projectId) return null;

    return (
      projectCatalog.find(
        (project) =>
          String(project.projectId) === String(selectedTeam.projectId),
      ) || null
    );
  }, [projectCatalog, selectedTeam]);

  const selectedTeamClientId =
    selectedTeam?.clientId ?? getProjectClientId(selectedTeamProject) ?? "";

  const selectedTeamClient = useMemo(
    () =>
      clients.find(
        (client) => String(client.clientId) === String(selectedTeamClientId),
      ) || null,
    [clients, selectedTeamClientId],
  );

  const teamForDetails = useMemo(() => {
    if (!selectedTeam) return null;

    return {
      ...selectedTeam,
      clientId: selectedTeamClientId,
      clientName:
        selectedTeam.clientName ||
        selectedTeamClient?.clientName ||
        selectedTeamProject?.clientName ||
        "",
      projectName:
        selectedTeam.projectName || selectedTeamProject?.projectName || "",
    };
  }, [selectedTeam, selectedTeamClient, selectedTeamClientId, selectedTeamProject]);

  const editProjectOptions = useMemo(() => {
    const clientId = editForm.clientId || selectedTeamClientId;
    const options = clientId
      ? projectCatalog.filter(
          (project) => String(getProjectClientId(project)) === String(clientId),
        )
      : projectCatalog;
    const hasSelectedProject = options.some(
      (project) => String(project.projectId) === String(editForm.projectId),
    );

    if (hasSelectedProject || !teamForDetails?.projectId) return options;

    return [
      {
        projectId: teamForDetails.projectId,
        projectName:
          teamForDetails.projectName || `Project ${teamForDetails.projectId}`,
        clientId: teamForDetails.clientId,
      },
      ...options,
    ];
  }, [editForm.clientId, editForm.projectId, projectCatalog, selectedTeamClientId, teamForDetails]);

  const filteredTeams = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return teams;

    return teams.filter((team) => {
      const project = projectCatalog.find(
        (item) => String(item.projectId) === String(team.projectId),
      );
      const clientId = team.clientId ?? getProjectClientId(project);
      const client = clients.find(
        (item) => String(item.clientId) === String(clientId),
      );

      return [
        team.teamName,
        team.teamId,
        team.projectId,
        team.projectName || project?.projectName,
        team.clientName || client?.clientName || project?.clientName,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term),
      );
    });
  }, [clients, projectCatalog, searchTerm, teams]);

  const loadTeams = useCallback(async () => {
    setLoadingTeams(true);

    try {
      const list = await getAllTeams();
      setTeams(list);
    } catch (error) {
      setTeams([]);
      setSnackbar({
        open: true,
        message: error?.message || "Failed to load teams",
        severity: "error",
      });
    } finally {
      setLoadingTeams(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);

    try {
      const response = await getUsersByClient();
      const data = response?.data;
      const apiUsers = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const normalizedUsers = apiUsers.map(normalizeApiUser).filter(Boolean);

      const nextProfileImageUrls = [];
      const usersWithProfileImages = await Promise.all(
        normalizedUsers.map(async (user) => {
          const profileImageUrl = await loadProfileImageUrl(user.uid);

          if (profileImageUrl) {
            nextProfileImageUrls.push(profileImageUrl);
          }

          return {
            ...user,
            profileImageUrl,
          };
        }),
      );

      revokeProfileImageUrls();
      profileImageUrlsRef.current = nextProfileImageUrls;
      setUsers(usersWithProfileImages);
    } catch (error) {
      revokeProfileImageUrls();
      setUsers([]);
      setSnackbar({
        open: true,
        message: error?.message || "Failed to load users",
        severity: "error",
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [revokeProfileImageUrls]);

  const loadProjectCatalog = useCallback(async (clientList) => {
    if (!clientList.length) {
      setProjectCatalog([]);
      return;
    }

    const projectGroups = await Promise.all(
      clientList.map(async (client) => {
        try {
          const list = await getProjectByClientId(client.clientId);
          return list.map((project) => ({
            ...project,
            clientId: getProjectClientId(project) ?? client.clientId,
            clientName: client.clientName,
          }));
        } catch {
          return [];
        }
      }),
    );

    setProjectCatalog(projectGroups.flat());
  }, []);

  useEffect(() => {
    const loadClients = async () => {
      setLoadingClients(true);

      try {
        const list = await getAllClientActivate();
        setClients(list);
        await loadProjectCatalog(list);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || "Failed to load clients",
          severity: "error",
        });
      } finally {
        setLoadingClients(false);
      }
    };

    loadClients();
  }, [loadProjectCatalog]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    setSelectedUserIds([]);

    if (tabIndex !== 0) {
      setUsers([]);
      revokeProfileImageUrls();
      return;
    }

    loadUsers();
  }, [loadUsers, revokeProfileImageUrls, tabIndex]);

  useEffect(() => {
    return () => revokeProfileImageUrls();
  }, [revokeProfileImageUrls]);

  useEffect(() => {
    if (activeTeamAction?.mode !== "edit" || !teamForDetails) return;

    setEditForm({
      clientId: teamForDetails.clientId ?? "",
      projectId: teamForDetails.projectId ?? "",
      teamName: teamForDetails.teamName ?? "",
    });
  }, [activeTeamAction?.mode, activeTeamAction?.teamId, teamForDetails]);

  const handleCreateTeam = async (event) => {
    event.preventDefault();

    if (!selectedClientId || !teamName.trim()) {
      setSnackbar({
        open: true,
        message: "Please select client and enter team name",
        severity: "warning",
      });
      return;
    }

    setCreating(true);

    const trimmedTeamName = teamName.trim();
    const newTeam = {
      teamId: `local-${Date.now()}`,
      teamName: trimmedTeamName,
      clientId: selectedClientId,
      clientName: selectedClient?.clientName || "",
      projectId: null,
      projectName: "",
    };

    setTeams((prev) => [newTeam, ...prev]);
    setTeamName("");
    setSelectedUserIds([]);
    setTabIndex(1);
    setSnackbar({
      open: true,
      message: "Team created successfully",
      severity: "success",
    });
    setCreating(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateTeam = async (event) => {
    event.preventDefault();

    if (!teamForDetails?.teamId || !editForm.projectId || !editForm.teamName.trim()) {
      setSnackbar({
        open: true,
        message: "Please select project and enter team name",
        severity: "warning",
      });
      return;
    }

    setUpdatingTeam(true);

    try {
      await updateTeam({
        teamId: teamForDetails.teamId,
        teamName: editForm.teamName,
        projectId: editForm.projectId,
      });

      await loadTeams();
      setActiveTeamAction({
        teamId: teamForDetails.teamId,
        mode: "view",
      });
      setSnackbar({
        open: true,
        message: "Team updated successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to update team",
        severity: "error",
      });
    } finally {
      setUpdatingTeam(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleTeamAction = (teamKey, mode) => {
    setActiveTeamAction({
      teamId: teamKey,
      mode,
    });
  };

  return (
    <ManagementPage
      title="Team Management"
      icon={<GroupsOutlinedIcon />}
      tabs={TEAM_TABS}
      tabIndex={tabIndex}
      onTabChange={setTabIndex}
      snackbar={snackbar}
      onSnackbarClose={handleSnackbarClose}
    >
          {tabIndex === 0 && (
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 550,
                mx: "auto",
                mt: 2.5,
                p: 2.5,
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                color: "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <GroupAddOutlinedIcon
                  sx={{ mr: 1, fontSize: 22, color: "#facc15" }}
                />
                <Typography
                  fontWeight={600}
                  fontSize={17}
                  letterSpacing={0.5}
                  sx={{ textTransform: "uppercase" }}
                >
                  Create Team
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.1)" }} />

              <Stack component="form" spacing={2.5} onSubmit={handleCreateTeam}>
                <FormControl fullWidth sx={fieldSx} disabled={loadingClients}>
                  <InputLabel id="team-client-label">Client</InputLabel>
                  <Select
                    labelId="team-client-label"
                    value={selectedClientId}
                    label="Client"
                    onChange={(event) => setSelectedClientId(event.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client.clientId} value={client.clientId}>
                        {client.clientName || `Client ${client.clientId}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Team Name"
                  value={teamName}
                  onChange={(event) => setTeamName(event.target.value)}
                  placeholder="Enter team name"
                  sx={fieldSx}
                />

                <FormControl fullWidth sx={fieldSx} disabled={loadingUsers}>
                  <InputLabel id="team-user-label">User</InputLabel>
                  <Select
                    labelId="team-user-label"
                    multiple
                    displayEmpty
                    value={selectedUserIds}
                    label="User"
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedUserIds(
                        typeof value === "string" ? value.split(",") : value,
                      );
                    }}
                    renderValue={() =>
                      selectedUsers.length ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0, width: "100%", overflow: "hidden" }}
                        >
                          <Stack direction="row" spacing={-0.6} sx={{ flexShrink: 0 }}>
                            {selectedUsers.slice(0, 3).map((user) => (
                              <Avatar
                                key={user.uid}
                                src={user.profileImageUrl || undefined}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: 12,
                                  border: "1px solid rgba(255,255,255,0.35)",
                                }}
                              >
                                {getUserLabel(user).charAt(0)}
                              </Avatar>
                            ))}
                          </Stack>
                          <Typography noWrap fontSize={14} sx={{ minWidth: 0 }}>
                            {selectedUsers.map(getUserLabel).join(", ")}
                          </Typography>
                        </Stack>
                      ) : (
                        ""
                      )
                    }
                    MenuProps={selectMenuProps}
                  >
                    {loadingUsers ? (
                      <MenuItem disabled>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} />
                          <Typography fontSize={13}>Loading users...</Typography>
                        </Stack>
                      </MenuItem>
                    ) : users.length === 0 ? (
                      <MenuItem disabled>
                        <Typography fontSize={13}>No users found</Typography>
                      </MenuItem>
                    ) : (
                      users.map((user) => (
                        <MenuItem key={user.uid} value={user.uid}>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Checkbox
                              checked={selectedUserIds.some(
                                (userId) => String(userId) === String(user.uid),
                              )}
                              sx={{
                                color: "rgba(255,255,255,0.65)",
                                p: 0,
                                "&.Mui-checked": {
                                  color: "#facc15",
                                },
                              }}
                            />
                            <Avatar
                              src={user.profileImageUrl || undefined}
                              sx={{ width: 28, height: 28, fontSize: 12 }}
                            >
                              {getUserLabel(user).charAt(0)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontSize={13} noWrap>
                                {getUserLabel(user)}
                              </Typography>
                              {user.uname && (
                                <Typography
                                  fontSize={11}
                                  sx={{ color: "rgba(255,255,255,0.55)" }}
                                  noWrap
                                >
                                  @{user.uname}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {selectedClient && (
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.62)" }}
                  >
                    Creating team for {selectedClient.clientName}
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    creating ||
                    loadingClients ||
                    !selectedClientId ||
                    !teamName.trim()
                  }
                  sx={{
                    width: "180px",
                    mt: 1,
                    alignSelf: "center",
                    py: 1.2,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    background: "#4f8a3d",
                    boxShadow: "0 4px 14px 0 rgba(79, 138, 61, 0.4)",
                    "&:hover": {
                      background: "#3f6f31",
                      boxShadow: "0 6px 20px 0 rgba(79, 138, 61, 0.5)",
                    },
                    "&.Mui-disabled": {
                      background: "rgba(255,255,255,0.16)",
                      color: "rgba(255,255,255,0.45)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {creating ? (
                    <CircularProgress size={22} sx={{ color: "white" }} />
                  ) : (
                    "Create Team"
                  )}
                </Button>
              </Stack>
            </Paper>
          )}

          {tabIndex === 1 && (
            <Box sx={{ p: 1.5 }}>
              <DynamicSearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search teams..."
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: { xs: "none", md: "56vh" },
                    overflowY: { xs: "visible", md: "auto" },
                    pr: { xs: 0, md: 1 },
                    "&::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(255,255,255,0.3)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "rgba(255,255,255,0.5)",
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(255,255,255,0.3) transparent",
                  }}
                >
                  {loadingTeams ? (
                    <Box display="flex" justifyContent="center" minHeight="28vh">
                      <CircularProgress size={26} sx={{ color: "white" }} />
                    </Box>
                  ) : filteredTeams.length === 0 ? (
                    <Typography
                      align="center"
                      fontSize={13}
                      sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}
                    >
                      {teams.length === 0 ? "No teams found." : "No teams match your search."}
                    </Typography>
                  ) : (
                    filteredTeams.map((team, index) => {
                      const teamKey = getTeamKey(team, index);
                      const isSelected =
                        String(activeTeamAction?.teamId) === String(teamKey);
                      const isViewActive =
                        activeTeamAction?.mode === "view" && isSelected;
                      const isEditActive =
                        activeTeamAction?.mode === "edit" && isSelected;

                      return (
                        <Card
                          key={teamKey}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            border: isSelected
                              ? "1px solid rgba(255,255,255,0.35)"
                              : "1px solid rgba(255,255,255,0.15)",
                            backdropFilter: "blur(6px)",
                            background: isSelected
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(255,255,255,0.05)",
                            color: "white",
                          }}
                        >
                          <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.15)",
                                  width: 30,
                                  height: 30,
                                }}
                              >
                                <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
                              </Avatar>

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography fontWeight={600} fontSize={13}>
                                  {team.teamName || `Team ${index + 1}`}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>

                          <CardActions
                            sx={{
                              justifyContent: "flex-end",
                              py: 0.5,
                              px: 1.5,
                              pt: 0,
                            }}
                          >
                            <Tooltip title="View">
                              <Button
                                size="small"
                                startIcon={
                                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={() => handleTeamAction(teamKey, "view")}
                                sx={actionButtonSx}
                                variant={isViewActive ? "contained" : "outlined"}
                              >
                                View
                              </Button>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <Button
                                size="small"
                                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleTeamAction(teamKey, "edit")}
                                sx={actionButtonSx}
                                variant={isEditActive ? "contained" : "outlined"}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                          </CardActions>
                        </Card>
                      );
                    })
                  )}
                </Box>

                <Box sx={{ flex: 1 }}>
                  {teamForDetails ? (
                    <Paper
                      sx={{
                        borderRadius: 2,
                        background: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(6px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                      }}
                    >
                      <TeamDetails
                        team={teamForDetails}
                        mode={activeTeamAction?.mode}
                        clients={clients}
                        projectOptions={editProjectOptions}
                        editForm={editForm}
                        fieldSx={fieldSx}
                        selectMenuProps={selectMenuProps}
                        updating={updatingTeam}
                        onEditFormChange={handleEditFormChange}
                        onUpdateTeam={handleUpdateTeam}
                      />
                    </Paper>
                  ) : (
                    <Typography
                      align="center"
                      fontSize={13}
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        width: "100%",
                        pt: { xs: 2, md: 0 },
                      }}
                    >
                      Select a team to view details
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
    </ManagementPage>
  );
}
